// Returns — the three stages of taking a shipment back:
//   RTO INWARD  scan the AWB, record what came back           (no stock moves)
//   QC          set a disposition per line                    (no stock moves)
//   RESTOCK     post it: Good → sellable, Damaged → DAMAGE,
//               discrepancies logged with qty 0
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X, RotateCcw, ClipboardCheck, PackagePlus, AlertTriangle, CheckCircle2, PackageSearch, Loader2 } from 'lucide-react';
import { api } from '@/api/client';
import { ExportImportBar } from '@/components/ui/ExportImportBar';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import {
  Badge, F, WmsCard, WmsDateRange, WmsPageBar, WmsSearch,
  productOptions, useAllProducts, useActiveWarehouses, useSearchPaged, useWmsMerchants,
  useWmsViewOnly, wmsInDateRange,
} from './WmsShared';

// What the warehouse found in the box. Only RECEIVED goes back to sellable stock; DAMAGED goes to the
// DAMAGE bin; the rest are discrepancies that stock nothing, and the two that imply someone made a
// mistake (or took something) must be explained in writing.
const DISPOSITIONS = [
  { value: 'RECEIVED', label: 'Received — back to sellable stock' },
  { value: 'DAMAGED', label: 'Damaged — DAMAGE bin' },
  { value: 'DIFFERENT_PRODUCT', label: 'Different product received — not stocked' },
  { value: 'PRODUCT_MISSING', label: 'Product missing — not stocked' },
  { value: 'BLANK_BOX', label: 'Blank / empty box — not stocked' },
];
// Dispositions the backend refuses to save without remarks.
const REMARKS_REQUIRED = ['DIFFERENT_PRODUCT', 'PRODUCT_MISSING'];

function useReturns() {
  return useQuery({ queryKey: ['wms-returns'], queryFn: async () => (await api.get('/wms/returns', { params: { limit: 1000 } })).data as any[] });
}

// ───────────────────────── STAGE 1 — RTO INWARD ─────────────────────────

export function RtoInwardTab() {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [show, setShow] = useState(false);
  const [active, setActive] = useState<any>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data = [] } = useReturns();
  const { data: merchants = [] } = useWmsMerchants();
  const mName = (id: any) => (merchants as any[]).find((m) => m.id === id)?.companyName ?? (id ? `M#${id}` : '—');

  const rows = (data as any[]).filter((r) => wmsInDateRange(r.returnDate ?? r.createdAt, from, to));
  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    rows,
    (r: any, t) => [r.returnNo, r.awbNo, r.orderNo, r.invoiceNumber, r.customerName, r.courier, r.status].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  const refresh = () => { qc.invalidateQueries({ queryKey: ['wms-returns'] }); qc.invalidateQueries({ queryKey: ['wms-stock'] }); };

  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><RotateCcw size={16} /> RTO Inward — record returned shipments</span>}
      actions={
        <>
          <WmsSearch value={q} onChange={setQ} placeholder="Search return / AWB / customer" className="w-72" />
          <WmsDateRange from={from} to={to} onFrom={(v) => { setFrom(v); setPage(1); }} onTo={(v) => { setTo(v); setPage(1); }} label="Return date" />
          {!viewOnly && <ExportImportBar resource="wms/returns" onImported={refresh} />}
          {!viewOnly && <button onClick={() => setShow(true)} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"><Plus size={14} /> New RTO Inward</button>}
        </>
      }
    >
      <p className="text-xs text-slate-500 mb-3">Inwarding only records what came back — nothing enters inventory until the return is QC'd and restocked.</p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">Return No</th><th className="text-left">AWB</th><th className="text-left">Order</th>
            {!viewOnly && <th className="text-left">Merchant</th>}
            <th className="text-left">Customer</th><th className="text-right">Qty</th>
            <th className="text-right">Good</th><th className="text-right">Bad</th><th className="text-right">Discrepancy</th>
            <th>Status</th><th>Date</th><th />
          </tr></thead>
          <tbody>
            {total === 0
              ? <tr><td colSpan={viewOnly ? 11 : 12} className="text-center py-6 text-slate-400">{q || from || to ? 'No matching returns' : 'No returns yet'}</td></tr>
              : pageRows.map((r: any) => (
                <tr key={r.id} className="border-t cursor-pointer hover:bg-slate-50" onClick={() => setActive(r)}>
                  <td className="p-2 font-mono text-xs">{r.returnNo}</td>
                  <td className="font-mono text-xs">{r.awbNo ?? '—'}</td>
                  <td className="text-xs">{r.orderNo ?? '—'}</td>
                  {!viewOnly && <td className="text-xs">{mName(r.merchantId)}</td>}
                  <td className="text-xs">{r.customerName ?? '—'}</td>
                  <td className="text-right">{r.totalQty}</td>
                  <td className="text-right text-emerald-700">{r.goodQty || '—'}</td>
                  <td className="text-right text-red-600">{r.badQty || '—'}</td>
                  <td className="text-right text-purple-700">{r.discrepancyQty || '—'}</td>
                  <td>
                    <Badge text={r.status} />
                    {/* The backend has always flagged a return that matches no dispatch; it was
                        never shown, so nobody could act on it before restocking. */}
                    {r.verifyStatus === 'NO_MATCH' && (
                      <span title="No matching dispatch found — verify before restocking"
                        className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700">
                        <AlertTriangle size={9} /> No dispatch
                      </span>
                    )}
                  </td>
                  <td className="text-xs text-slate-500">{(r.returnDate ?? r.createdAt)?.slice(0, 10)}</td>
                  <td className="text-xs text-emerald-700">Open →</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      {show && <RtoInwardForm onClose={() => setShow(false)} onSaved={() => { setShow(false); refresh(); }} />}
      {active && <ReturnModal ret={active} stage="view" viewOnly={viewOnly} onClose={() => setActive(null)} onSaved={() => { refresh(); setActive(null); }} />}
    </WmsCard>
  );
}

function RtoInwardForm({ onClose, onSaved }: any) {
  const qcClient = useQueryClient();
  const [form, setForm] = useState<any>({ items: [{ productId: '', returnedQty: 1 }] });
  // Which line (if any) is having a product created for it right now.
  const [newProductFor, setNewProductFor] = useState<number | null>(null);
  const products = useAllProducts();
  const options = productOptions(products);
  const { data: merchants = [] } = useWmsMerchants();
  const { data: warehouses = [] } = useActiveWarehouses();

  /**
   * Was anything actually dispatched against this AWB / invoice?
   *
   * The save has always run this check and flagged the return NO_MATCH — but only after the fact,
   * so the person at the dock learned about it days later on the QC screen. Asking the same
   * question live means a mis-read AWB gets fixed while the parcel is still in hand.
   */
  const productIds = form.items.map((i: any) => i.productId).filter(Boolean).join(',');
  const canCheck = !!form.merchantId && !!form.warehouseId && !!((form.awbNo ?? '').trim() || (form.invoiceNumber ?? '').trim());
  const { data: check, isFetching: checking } = useQuery({
    queryKey: ['wms-return-check', form.merchantId, form.warehouseId, form.awbNo, form.invoiceNumber, productIds],
    queryFn: async () => (await api.get('/wms/returns/check-dispatch', {
      params: {
        merchantId: form.merchantId, warehouseId: form.warehouseId,
        awbNo: form.awbNo ?? '', invoiceNumber: form.invoiceNumber ?? '', productIds,
      },
    })).data as any,
    enabled: canCheck,
    staleTime: 30_000,
  });

  const setItem = (i: number, k: string, v: any) => setForm((f: any) => ({ ...f, items: f.items.map((x: any, idx: number) => idx === i ? { ...x, [k]: v } : x) }));
  const addRow = () => setForm((f: any) => ({ ...f, items: [...f.items, { productId: '', returnedQty: 1 }] }));
  const rmRow = (i: number) => setForm((f: any) => ({ ...f, items: f.items.filter((_: any, idx: number) => idx !== i) }));

  const save = useMutation({
    mutationFn: async () => (await api.post('/wms/returns', form)).data,
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not save the return'),
  });
  const canSave = !!form.merchantId && !!form.warehouseId && !!(form.awbNo ?? '').trim()
    && form.items.length > 0 && form.items.every((i: any) => i.productId && Number(i.returnedQty) > 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[920px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">New RTO Inward</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <F label="AWB / LR No *"><input value={form.awbNo ?? ''} onChange={(e) => setForm({ ...form, awbNo: e.target.value })} placeholder="Scan or paste AWB" className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F>
            <F label="Merchant *"><select value={form.merchantId ?? ''} onChange={(e) => setForm({ ...form, merchantId: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(merchants as any[]).map((m) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}</select></F>
            <F label="Warehouse *"><select value={form.warehouseId ?? ''} onChange={(e) => setForm({ ...form, warehouseId: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="">—</option>{(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}</select></F>
            <F label="Return Date"><input type="date" value={form.returnDate ?? ''} onChange={(e) => setForm({ ...form, returnDate: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Order No"><input value={form.orderNo ?? ''} onChange={(e) => setForm({ ...form, orderNo: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Invoice Number"><input value={form.invoiceNumber ?? ''} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Customer Name"><input value={form.customerName ?? ''} onChange={(e) => setForm({ ...form, customerName: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Courier"><input value={form.courier ?? ''} onChange={(e) => setForm({ ...form, courier: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
            <F label="Received By"><input value={form.receivedByName ?? ''} onChange={(e) => setForm({ ...form, receivedByName: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>
          </div>
          <F label="Remarks"><input value={form.remarks ?? ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></F>

          {/* Verdict on the AWB, live. Amber is not a refusal — the goods are on the dock either
              way — it is a "check this before you restock it". */}
          {canCheck && (
            checking ? (
              <div className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Checking the original dispatch…</div>
            ) : check?.checked && check?.matched ? (
              <div className="text-xs rounded border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-2 flex items-start gap-2">
                <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
                <span>
                  <b>Matched the original dispatch.</b>{' '}
                  {check.dispatch?.docketNumber ? `Docket ${check.dispatch.docketNumber}` : ''}
                  {check.dispatch?.invoiceNumber ? ` · Invoice ${check.dispatch.invoiceNumber}` : ''}
                  {check.dispatch?.skuCode ? ` · SKU ${check.dispatch.skuCode}` : ''}
                </span>
              </div>
            ) : check?.checked ? (
              <div className="text-xs rounded border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 flex items-start gap-2">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span><b>No matching dispatch found.</b> {check.detail}</span>
              </div>
            ) : null
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">Returned Items</div>
              <div className="text-[11px] text-slate-500">Product not in the list? Use <b>New product</b> on the line — it is created and selected without leaving this window.</div>
            </div>
            {form.items.map((it: any, i: number) => (
              <div key={i}>
                <div className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-4"><F label="Product *"><SearchableSelect value={it.productId || ''} onChange={(v) => setItem(i, 'productId', v)} options={options} placeholder="Search SKU / name…" /></F></div>
                  <div className="col-span-2">
                    <F label="&nbsp;">
                      <button type="button" onClick={() => setNewProductFor(newProductFor === i ? null : i)}
                        className="w-full border border-emerald-300 text-emerald-700 rounded px-2 py-1.5 text-xs flex items-center justify-center gap-1">
                        <PackageSearch size={12} /> {newProductFor === i ? 'Close' : 'New product'}
                      </button>
                    </F>
                  </div>
                  <div className="col-span-2"><F label="Returned Qty *"><input type="number" min={1} value={it.returnedQty} onChange={(e) => setItem(i, 'returnedQty', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
                  <div className="col-span-1"><F label="Batch"><input value={it.batchNo ?? ''} onChange={(e) => setItem(i, 'batchNo', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F></div>
                  <div className="col-span-2"><F label="Note"><input value={it.qcRemarks ?? ''} onChange={(e) => setItem(i, 'qcRemarks', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
                  <div className="col-span-1">{form.items.length > 1 && <button onClick={() => rmRow(i)} className="border border-red-300 text-red-600 p-1.5 rounded"><Trash2 size={14} /></button>}</div>
                </div>
                {newProductFor === i && (
                  <NewProductInline
                    merchantId={form.merchantId}
                    warehouseId={form.warehouseId}
                    onCancel={() => setNewProductFor(null)}
                    onCreated={(prod: any) => {
                      // Refresh the catalogue everywhere at once: this same product has to be
                      // selectable on QC and Restock the moment it exists, not after a reload.
                      qcClient.invalidateQueries({ queryKey: ['wms-products-all'] });
                      qcClient.invalidateQueries({ queryKey: ['wms-products'] });
                      setItem(i, 'productId', prod.id);
                      setNewProductFor(null);
                    }}
                  />
                )}
              </div>
            ))}
            <button onClick={addRow} className="text-emerald-700 border border-emerald-300 px-3 py-1 rounded text-xs flex items-center gap-1"><Plus size={12} /> Add line</button>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-3">
          <div className="text-xs text-slate-500 mr-auto">Nothing is added to inventory yet — the return goes to QC next.</div>
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !canSave} title={!canSave ? 'AWB, Merchant, Warehouse and a product on every line are required' : undefined} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save RTO Inward'}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Create a product without leaving the RTO window.
 *
 * A parcel comes back with something that was never in the catalogue — a sample, a replacement, an
 * item the merchant added upstream — and the alternative was to abandon the inward, go to Products,
 * create it, and start again. The product is created against the SAME merchant and warehouse the
 * return is being booked to, so it lands where the stock will.
 */
function NewProductInline({ merchantId, warehouseId, onCancel, onCreated }: any) {
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // A SKU nobody typed is a SKU nobody has to invent — derived from the name, still editable.
  const suggested = name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24);

  const create = useMutation({
    mutationFn: async () => (await api.post('/wms/products', {
      name: name.trim(),
      sku: (sku.trim() || suggested),
      merchantId: merchantId || null,
      warehouseId: warehouseId || null,
    })).data,
    onSuccess: (p: any) => onCreated(p),
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not create the product'),
  });

  const canSave = !!name.trim() && !!(sku.trim() || suggested) && !!merchantId;

  return (
    <div className="border border-emerald-200 bg-emerald-50/50 rounded p-3 mb-3">
      <div className="text-xs font-medium text-emerald-800 mb-2">New product — added to this merchant&apos;s catalogue</div>
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-5"><F label="Product Name *"><input autoFocus value={name} onChange={(e) => { setName(e.target.value); setErr(null); }} placeholder="e.g. Cotton Bedsheet Double Blue" className="w-full border rounded px-2 py-1.5 text-sm" /></F></div>
        <div className="col-span-4"><F label="SKU *"><input value={sku} onChange={(e) => { setSku(e.target.value.toUpperCase()); setErr(null); }} placeholder={suggested || 'SKU-001'} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></F></div>
        <div className="col-span-3 flex gap-2">
          <button type="button" onClick={onCancel} className="border px-3 py-1.5 rounded text-xs">Cancel</button>
          <button type="button" onClick={() => create.mutate()} disabled={!canSave || create.isPending}
            className="bg-emerald-600 text-white px-3 py-1.5 rounded text-xs disabled:opacity-50 flex items-center gap-1">
            {create.isPending && <Loader2 size={12} className="animate-spin" />} Create &amp; use
          </button>
        </div>
      </div>
      {!merchantId && <div className="text-[11px] text-amber-700 mt-1">Pick the merchant above first — a product belongs to one.</div>}
      {err && <div className="text-[11px] text-red-600 mt-1">{err}</div>}
    </div>
  );
}

// ───────────────────── STAGE 2 & 3 — QC and RESTOCK worklists ─────────────────────

export function ReturnsQcTab() {
  return (
    <ReturnsWorklist
      stage="qc"
      title="Returns QC — decide what came back"
      subtitle="Set a disposition per line. Good and Damaged will be stocked at Restock; 'different product' and 'missing parts' are logged only."
      emptyText="Nothing waiting for QC"
      isPending={(r: any) => r.status === 'RTO_INWARD'}
      Icon={ClipboardCheck}
    />
  );
}

export function RestockTab() {
  return (
    <ReturnsWorklist
      stage="restock"
      title="Restock — post QC'd returns into inventory"
      subtitle="Good → available (sellable), Rejected/Damaged → DAMAGE bin, different product / missing parts → logged with no stock."
      emptyText="Nothing waiting to be restocked"
      isPending={(r: any) => r.status === 'QC'}
      Icon={PackagePlus}
    />
  );
}

function ReturnsWorklist({ stage, title, subtitle, emptyText, isPending, Icon }: any) {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [active, setActive] = useState<any>(null);
  const [showDone, setShowDone] = useState(false);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data = [] } = useReturns();
  const { data: merchants = [] } = useWmsMerchants();
  const mName = (id: any) => (merchants as any[]).find((m) => m.id === id)?.companyName ?? (id ? `M#${id}` : '—');

  const dateFiltered = (data as any[]).filter((r) => wmsInDateRange(r.returnDate ?? r.createdAt, from, to));
  const rows = dateFiltered.filter((r) => (showDone ? true : isPending(r)) && r.status !== 'CANCELLED');
  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    rows,
    (r: any, t) => [r.returnNo, r.awbNo, r.customerName, r.status].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  const refresh = () => { qc.invalidateQueries({ queryKey: ['wms-returns'] }); qc.invalidateQueries({ queryKey: ['wms-stock'] }); };

  const bulk = useMutation({
    mutationFn: async () => (await api.post('/wms/returns/bulk-restock', { ids: Array.from(sel) })).data,
    onSuccess: (r: any) => {
      setSel(new Set()); refresh();
      alert(`Restocked ${r.passed} return(s)${r.failed ? `, ${r.failed} failed:\n${(r.errors ?? []).map((e: any) => `#${e.id}: ${e.reason}`).join('\n')}` : '.'}`);
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Bulk restock failed'),
  });
  const toggle = (id: number) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><Icon size={16} /> {title}</span>}
      actions={
        <>
          <WmsSearch value={q} onChange={setQ} placeholder="Search return / AWB / customer" className="w-72" />
          <WmsDateRange from={from} to={to} onFrom={(v) => { setFrom(v); setPage(1); }} onTo={(v) => { setTo(v); setPage(1); }} label="Return date" />
        </>
      }
    >
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="text-xs text-slate-500">{subtitle}</span>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-auto">
          <input type="checkbox" checked={showDone} onChange={(e) => { setShowDone(e.target.checked); setPage(1); setSel(new Set()); }} />
          Show completed too
        </label>
        {!viewOnly && stage === 'restock' && sel.size > 0 && (
          <button onClick={() => bulk.mutate()} disabled={bulk.isPending} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50">
            {bulk.isPending ? 'Restocking…' : `Restock selected (${sel.size})`}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            {!viewOnly && stage === 'restock' && <th className="p-2 w-8" />}
            <th className="p-2 text-left">Return No</th><th className="text-left">AWB</th>
            {!viewOnly && <th className="text-left">Merchant</th>}
            <th className="text-left">Customer</th><th className="text-right">Qty</th>
            <th className="text-right">Good</th><th className="text-right">Bad</th><th className="text-right">Discrepancy</th>
            <th>Status</th><th>Date</th><th />
          </tr></thead>
          <tbody>
            {total === 0
              ? <tr><td colSpan={12} className="text-center py-6 text-slate-400">{q ? 'No matches' : emptyText}</td></tr>
              : pageRows.map((r: any) => (
                <tr key={r.id} className="border-t cursor-pointer hover:bg-slate-50" onClick={() => setActive(r)}>
                  {!viewOnly && stage === 'restock' && (
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      {isPending(r) && <input type="checkbox" checked={sel.has(Number(r.id))} onChange={() => toggle(Number(r.id))} />}
                    </td>
                  )}
                  <td className="p-2 font-mono text-xs">{r.returnNo}</td>
                  <td className="font-mono text-xs">{r.awbNo ?? '—'}</td>
                  {!viewOnly && <td className="text-xs">{mName(r.merchantId)}</td>}
                  <td className="text-xs">{r.customerName ?? '—'}</td>
                  <td className="text-right">{r.totalQty}</td>
                  <td className="text-right text-emerald-700">{r.goodQty || '—'}</td>
                  <td className="text-right text-red-600">{r.badQty || '—'}</td>
                  <td className="text-right text-purple-700">{r.discrepancyQty || '—'}</td>
                  <td>
                    <Badge text={r.status} />
                    {/* The backend has always flagged a return that matches no dispatch; it was
                        never shown, so nobody could act on it before restocking. */}
                    {r.verifyStatus === 'NO_MATCH' && (
                      <span title="No matching dispatch found — verify before restocking"
                        className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700">
                        <AlertTriangle size={9} /> No dispatch
                      </span>
                    )}
                  </td>
                  <td className="text-xs text-slate-500">{(r.returnDate ?? r.createdAt)?.slice(0, 10)}</td>
                  <td className="text-xs text-emerald-700">{isPending(r) ? (stage === 'qc' ? 'QC →' : 'Restock →') : 'Open →'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
      {active && <ReturnModal ret={active} stage={stage} viewOnly={viewOnly} onClose={() => setActive(null)} onSaved={() => { refresh(); setActive(null); }} />}
    </WmsCard>
  );
}

// ─────────────────────────── the return detail modal ───────────────────────────

function ReturnModal({ ret, stage, viewOnly, onClose, onSaved }: any) {
  const { data: fresh } = useQuery({ queryKey: ['wms-return', ret.id], queryFn: async () => (await api.get(`/wms/returns/${ret.id}`)).data });
  const r = fresh ?? ret;
  const [items, setItems] = useState<any[]>(() => seed(ret));
  const [seededFor, setSeededFor] = useState<any>(null);
  if (fresh && seededFor !== fresh) { setSeededFor(fresh); setItems(seed(fresh)); }

  function seed(x: any) {
    return (x.items ?? []).map((i: any) => ({
      id: i.id, sku: i.product?.sku, name: i.product?.name, returnedQty: Number(i.returnedQty ?? 0),
      // Legacy rows saved under the old names still open on the matching option.
      disposition: i.disposition === 'PENDING' ? 'RECEIVED'
        : i.disposition === 'GOOD' ? 'RECEIVED'
        : i.disposition === 'MISSING_PARTS' ? 'PRODUCT_MISSING'
        : i.disposition,
      wasPending: i.disposition === 'PENDING',
      goodQty: Number(i.goodQty ?? 0), badQty: Number(i.badQty ?? 0),
      bin: i.bin ?? '', batchNo: i.batchNo ?? '', qcRemarks: i.qcRemarks ?? '', restocked: !!i.restocked,
    }));
  }

  const setIt = (idx: number, k: string, v: any) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, [k]: v } : x));
  const editable = stage === 'qc' && !viewOnly && r.status !== 'RESTOCKED' && r.status !== 'CANCELLED';
  // The backend refuses these without remarks; block the save here so it isn't a wasted round trip.
  const qcIncomplete = items.some((i) => !i.restocked && REMARKS_REQUIRED.includes(i.disposition) && !String(i.qcRemarks ?? '').trim());

  const saveQc = useMutation({
    mutationFn: async () => (await api.post(`/wms/returns/${r.id}/qc`, {
      items: items.filter((i) => !i.restocked).map((i) => ({
        id: i.id, disposition: i.disposition, bin: i.bin, batchNo: i.batchNo, qcRemarks: i.qcRemarks,
      })),
    })).data,
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'QC save failed'),
  });

  const restock = useMutation({
    mutationFn: async () => (await api.post(`/wms/returns/${r.id}/restock`)).data,
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Restock failed'),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[1000px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2">{r.returnNo} <Badge text={r.status} /></div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-4 gap-3 text-sm">
            <F label="AWB / LR"><div className="font-mono text-slate-700">{r.awbNo ?? '—'}</div></F>
            <F label="Order No"><div className="text-slate-700">{r.orderNo ?? '—'}</div></F>
            <F label="Customer"><div className="text-slate-700">{r.customerName ?? '—'}</div></F>
            <F label="Return Date"><div className="text-slate-700">{(r.returnDate ?? r.createdAt)?.slice(0, 10)}</div></F>
            <F label="Invoice No"><div className="text-slate-700">{r.invoiceNumber ?? '—'}</div></F>
            <F label="Verified against dispatch">
              <div className={r.dispatchId ? 'text-emerald-700' : 'text-red-600'}>
                {r.dispatchId ? `Matched dispatch #${r.dispatchId}` : 'No matching dispatch found'}
              </div>
            </F>
            <F label="Courier"><div className="text-slate-700">{r.courier ?? '—'}</div></F>
            <F label="Received By"><div className="text-slate-700">{r.receivedByName ?? '—'}</div></F>
            <F label="Remarks"><div className="text-slate-700">{r.remarks ?? '—'}</div></F>
          </div>

          {stage === 'restock' && r.status === 'QC' && (
            <div className="text-xs bg-amber-50 text-amber-800 rounded px-3 py-2">
              Restocking posts these lines into inventory. Received goes to the bin shown (sellable), Damaged to the DAMAGE bin;
              "different product", "product missing" and "blank box" are recorded in stock history without changing any quantity.
            </div>
          )}
          {stage === 'restock' && !r.dispatchId && (
            <div className="text-xs bg-red-50 text-red-700 rounded px-3 py-2">
              This return could not be matched to any dispatch for this merchant and warehouse — it cannot be restocked until the AWB / docket or the Invoice + SKU is corrected.
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-xs"><tr>
                <th className="p-2 text-left" style={{ minWidth: 220 }}>Product</th>
                <th className="text-right">Returned</th>
                <th className="text-left" style={{ minWidth: 240 }}>Disposition</th>
                <th className="text-right">Good</th><th className="text-right">Bad</th><th className="text-right">Discrepancy</th>
                <th className="text-left">Bin</th><th className="text-left">Batch</th><th className="text-left">Remarks</th>
              </tr></thead>
              <tbody>
                {items.map((i: any, idx: number) => {
                  // Preview what this disposition will stock, so the operator sees it before restocking.
                  const good = i.disposition === 'RECEIVED' ? i.returnedQty : 0;
                  const bad = i.disposition === 'DAMAGED' ? i.returnedQty : 0;
                  const disc = (good || bad) ? 0 : i.returnedQty;
                  const needsRemark = REMARKS_REQUIRED.includes(i.disposition) && !String(i.qcRemarks ?? '').trim();
                  return (
                    <tr key={i.id} className={`border-t ${needsRemark ? 'bg-rose-50' : ''}`}>
                      <td className="p-2">{i.sku} — {i.name}</td>
                      <td className="text-right">{i.returnedQty}</td>
                      <td>
                        {editable && !i.restocked
                          ? <select value={i.disposition} onChange={(e) => setIt(idx, 'disposition', e.target.value)} className="w-full border rounded px-1 py-1 text-sm">
                              {DISPOSITIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                            </select>
                          : <Badge text={i.wasPending && stage !== 'qc' ? 'PENDING' : i.disposition} />}
                      </td>
                      <td className="text-right text-emerald-700">{good || '—'}</td>
                      <td className="text-right text-red-600">{bad || '—'}</td>
                      <td className="text-right text-purple-700">{disc || '—'}</td>
                      <td>
                        {editable && !i.restocked
                          ? <input value={i.bin ?? ''} onChange={(e) => setIt(idx, 'bin', e.target.value)} placeholder="RTO" className="w-20 border rounded px-1 text-sm font-mono" />
                          : <span className="font-mono text-xs">{i.bin || '—'}</span>}
                      </td>
                      <td className="font-mono text-xs">{i.batchNo || '—'}</td>
                      <td>
                        {editable && !i.restocked
                          ? <input value={i.qcRemarks ?? ''} onChange={(e) => setIt(idx, 'qcRemarks', e.target.value)}
                              placeholder={needsRemark ? 'Required — what came back?' : 'Optional'}
                              className={`w-full border rounded px-1 text-sm ${needsRemark ? 'border-rose-400 bg-rose-50 placeholder-rose-500' : ''}`} />
                          : <span className="text-xs text-slate-500">{i.qcRemarks || '—'}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border px-4 py-1.5 rounded text-sm">Close</button>
          {editable && (
            <button onClick={() => saveQc.mutate()} disabled={saveQc.isPending || qcIncomplete}
              title={qcIncomplete ? 'Remarks are required for "different product" and "product missing" lines' : undefined}
              className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
              {saveQc.isPending ? 'Saving…' : 'Save QC'}
            </button>
          )}
          {stage === 'restock' && !viewOnly && r.status === 'QC' && (
            <button onClick={() => restock.mutate()} disabled={restock.isPending} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
              {restock.isPending ? 'Restocking…' : 'Restock → Inventory'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
